import axios from 'axios';
import {API_URL} from '../config/constants';
import {
    IrpBarrierAnalysisDTO,
    IrpNodeTabDTO,
    IrpReportTabDTO,
    IrpReportTableRowDTO,
    IrpWorkbookOverviewDTO,
} from '../@types/integrated-risk-picture';
import {RegisterRecommendationDTO} from '../@types/safety-recommendation';
import {ComponentMappingStatusSummary} from '../@types/component-mapping-state';
import SpringDataPage from '../@types/spring-data-page';
import {getErrorMessage} from '../utils/utils';

/**
 * The component workbook overview: the IRP workbook overview shape wrapped with the component
 * evidence lifecycle (backend `ComponentWorkbookOverviewDTO`).
 */
export interface ComponentWorkbookOverview {
    lastRunAt?: string;
    mappingStatus?: ComponentMappingStatusSummary;
    overview: IrpWorkbookOverviewDTO;
}

/**
 * Client for the component evidence workbook endpoints (backend
 * `ComponentWorkbookController`, base `/api/v2/components`), scoped to one
 * (component version, report source) pair. Every response reuses the IRP workbook DTO shapes so
 * the workbook UI renders component evidence unchanged.
 */
class ComponentWorkbookService {
    static baseUrl = API_URL + 'api/v2/components';

    private static workbookUrl(componentId: number | string, sourceName: string): string {
        return `${ComponentWorkbookService.baseUrl}/${componentId}/sources/${encodeURIComponent(sourceName)}/workbook`;
    }

    private static get<T>(url: string, failure: string): Promise<T> {
        return axios
            .get(url, {withCredentials: true})
            .then(response => response.data as T)
            .catch(err => {
                throw new Error(`${failure}: ` + getErrorMessage(err));
            });
    }

    /** Fetches the wrapped workbook overview for a (component, source) pair. */
    getWorkbookOverview(componentId: number | string, sourceName: string): Promise<ComponentWorkbookOverview> {
        return ComponentWorkbookService.get(
            `${ComponentWorkbookService.workbookUrl(componentId, sourceName)}/overview`,
            'Failed to load component workbook overview');
    }

    /** Fetches one page of the scope's evidenced reports as workbook table rows. */
    getWorkbookOverviewReports(
        componentId: number | string,
        sourceName: string,
        page: number,
        size: number,
    ): Promise<SpringDataPage<IrpReportTableRowDTO>> {
        const params = new URLSearchParams({page: String(page), size: String(size)});
        return ComponentWorkbookService.get(
            `${ComponentWorkbookService.workbookUrl(componentId, sourceName)}/overview/reports?${params.toString()}`,
            'Failed to load component workbook reports');
    }

    /** Fetches the report tab for one evidenced report. */
    getWorkbookReportTab(componentId: number | string, sourceName: string, reportId: number): Promise<IrpReportTabDTO> {
        return ComponentWorkbookService.get(
            `${ComponentWorkbookService.workbookUrl(componentId, sourceName)}/report/${reportId}`,
            'Failed to load component workbook report tab');
    }

    /** Fetches the node tab for one interior node. */
    getWorkbookNodeTab(componentId: number | string, sourceName: string, nodeId: number): Promise<IrpNodeTabDTO> {
        return ComponentWorkbookService.get(
            `${ComponentWorkbookService.workbookUrl(componentId, sourceName)}/node/${nodeId}`,
            'Failed to load component workbook node tab');
    }

    /** Fetches the barrier analysis for a (component, source) pair. */
    getWorkbookBarrierAnalysis(componentId: number | string, sourceName: string): Promise<IrpBarrierAnalysisDTO> {
        return ComponentWorkbookService.get(
            `${ComponentWorkbookService.workbookUrl(componentId, sourceName)}/barrier-analysis`,
            'Failed to load component workbook barrier analysis');
    }

    getWorkbookNtsbRecommendations(componentId: number | string, sourceName: string): Promise<RegisterRecommendationDTO[]> {
        return ComponentWorkbookService.get(
            `${ComponentWorkbookService.workbookUrl(componentId, sourceName)}/ntsb-recommendations`,
            'Failed to load component workbook NTSB recommendations');
    }
}

export default new ComponentWorkbookService();
